// cleanup_hoc_to_hooks_PAYMENT_MAPS
// cleanup_remove_external_libraries_PAYMENT_MAPS
// cleanup_convert_to_typescript_file_PAYMENT_MAPS

import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get, pick } from 'lodash';
import classnames from 'classnames';

import {
  ValidatedInput as TextInput,
  defaultStrings_en_US as defaultStringsFormComponents
} from '@glu/form-components';
import { ExclamationCircleIcon } from '@glu/icons-react';
import { Tooltip } from '@glu/tooltip-react';
import locale from '@glu/locale';
import { WholeNumber as Input, defaultStrings as defaultStringsEnUs } from '@glu/input-mask-react';

import { DEFAULT_PROPS, TYPES } from 'constants.js';
import useLocale from 'components/useLocale';
import Currency from 'components/CurrencyInput/CurrencyInput';

import useStyles from './Table.styles';
import {
  asyncValidateBankCode,
  validateDuplicatePositionField,
  validateLength,
  validateGraterThanOne,
  asyncExecuteMultiplyGluFunctions,
  requiredTransactionCodeValidator,
  requiredValidator,
  startingPositionGreaterRecordLengthValidation,
  endingPositionGreaterRecordLengthValidation,
  overlapValidation,
  isAmountAddValueFieldEmpty,
  validateAmountFieldLength,
  validateRangeLimitation
} from './utils';
import { PROCESS_MODES_NAMES } from '../utils';
import {
  getDelimitedFormatTableFields,
  getFixedFormatTableFields,
  HARDCODED_LENGTH_FIELDS_LENGTH,
  LENGTH_MAX_LENGTH,
  MAP_FORMAT,
  registerFixedFieldValidators,
  setInitialFieldsValidation,
  START_POSITION_MAX_LENGTH,
  validateLengthTableField
} from './utils.mapFormats';
import {
  getOptionalTableFields,
  getTableFields,
  NONE,
  POSITION_FIELD_MAX_LENGTH,
  TABLE_FIELDS,
  TRANSACTION_FIELD_VARIANT,
  TRAN_TYPES_DROPDOWN_DATA
} from './constants';
import TransactionTypeRow from './TransactionRows/TransactionTypesRow';
import TransactionCodeRow from './TransactionRows/TransactionCodeRow';
import SharedRowPart from './SharedRowPart';

locale.set(defaultStringsEnUs);
locale.set(defaultStringsFormComponents);

const { EMPTY_ARRAY, EMPTY_FUNCTION, EMPTY_OBJ, EMPTY_STRING, FALSE, NULL } = DEFAULT_PROPS;

export const TableComponent = ({
  accountTypesData,
  changeTransactionFieldVariant,
  controlledFormData,
  formState = EMPTY_OBJ,
  mapFormat = NULL,
  onChange = EMPTY_FUNCTION,
  processMode = EMPTY_STRING,
  registerValidators = EMPTY_FUNCTION,
  sending = FALSE,
  transactionCodesData = EMPTY_ARRAY,
  // has to be for componentDidUpdate
  // eslint-disable-next-line react/prop-types
  transactionFieldVariant,
  unregisterValidators = EMPTY_FUNCTION,
  validate = EMPTY_FUNCTION
}) => {
  const classes = useStyles();
  const locale = useLocale();

  const tableDopdownsData = useMemo(
    () => ({
      [TABLE_FIELDS.TRAN_CODE]: transactionCodesData,
      [TABLE_FIELDS.TRAN_TYPE]: TRAN_TYPES_DROPDOWN_DATA,
      [TABLE_FIELDS.ACCOUNT_TYPE]: accountTypesData
    }),
    [transactionCodesData, accountTypesData]
  );

  const tableFields = useMemo(
    () => getTableFields(locale, tableDopdownsData),
    [locale, tableDopdownsData]
  );

  const isModeUpdateOnly = useMemo(
    () => processMode === PROCESS_MODES_NAMES.UPDATE_ONLY,
    [processMode]
  );
  const isModeAddAndUpdate = useMemo(
    () => processMode === PROCESS_MODES_NAMES.ADD_AND_UPDATE,
    [processMode]
  );

  const isAmountAddValueFieldEmptyMemoized = useMemo(
    () => isAmountAddValueFieldEmpty(formState),
    [formState]
  );

  const requiredTransactionCodeValidatorFunc = useMemo(
    () => (!isModeUpdateOnly ? requiredTransactionCodeValidator(locale) : []),
    [locale, isModeUpdateOnly]
  );

  const lengthFieldValidators = useCallback(
    (field, ignoreOptional) => [
      ...(field.fixedFormatLength
        ? [validateLengthTableField(locale, field.fixedFormatLength)]
        : []),
      ...(field.fixedFormatLengthLimitation
        ? [validateRangeLimitation(locale, field.fixedFormatLengthLimitation)]
        : []),
      validateGraterThanOne(locale),
      validateLength(LENGTH_MAX_LENGTH, locale),
      endingPositionGreaterRecordLengthValidation(locale),
      overlapValidation(locale),
      ...(!isModeUpdateOnly && (!field.optional || ignoreOptional)
        ? [requiredValidator(locale)]
        : [])
    ],
    [isModeUpdateOnly, locale]
  );

  const startPositionFieldValidators = useCallback(
    (field, ignoreOptional) => [
      validateGraterThanOne(locale),
      validateLength(START_POSITION_MAX_LENGTH, locale),
      startingPositionGreaterRecordLengthValidation(locale),
      overlapValidation(locale),
      ...(!isModeUpdateOnly && (!field.optional || ignoreOptional)
        ? [requiredValidator(locale)]
        : [])
    ],
    [isModeUpdateOnly, locale]
  );

  const positionFieldValidators = useCallback(
    (field, ignoreOptional) => [
      validateGraterThanOne(locale),
      validateLength(POSITION_FIELD_MAX_LENGTH, locale),
      validateDuplicatePositionField(locale),
      ...(!isModeUpdateOnly && (!field.optional || ignoreOptional)
        ? [requiredValidator(locale)]
        : [])
    ],
    [isModeUpdateOnly, locale]
  );

  const addValueValidators = useCallback(
    (field, maxLengthIndicatorLimit) => [
      ...(maxLengthIndicatorLimit
        ? [
            field.id === TABLE_FIELDS.AMOUNT
              ? validateAmountFieldLength(locale)
              : validateLength(maxLengthIndicatorLimit, locale)
          ]
        : []),
      ...(!isModeUpdateOnly && !field.optional ? [requiredValidator(locale)] : []),
      ...(field.id === TABLE_FIELDS.BANK_CODE
        ? [
            {
              fn: asyncValidateBankCode(field?.maxLength),
              msg: locale['wireImports.invalidBankCode']
            }
          ]
        : [])
    ],
    [isModeUpdateOnly, locale]
  );

  const onPositionFieldFocus = useCallback(
    ({ target: { name } }) => {
      if (!formState[name]) {
        const tableFieldData = tableFields.find((field) => field.id === name);

        registerValidators(tableFieldData.id, positionFieldValidators(tableFieldData));

        unregisterValidators(`${tableFieldData.id}_addValue`);
        return validate(`${tableFieldData.id}_addValue`);
      }
      return null;
    },
    [
      validate,
      unregisterValidators,
      registerValidators,
      positionFieldValidators,
      formState,
      tableFields
    ]
  );

  const onOptionalFixedPositionBlur = useCallback(
    (name) => {
      const fieldSplitted = name?.split('_');
      const currentFieldName = fieldSplitted[0];
      const fieldEnding = fieldSplitted[fieldSplitted.length - 1];
      const OPTIONAL_TABLE_FIELDS = getOptionalTableFields(locale);

      const thisOptionalField = OPTIONAL_TABLE_FIELDS.find(
        (field) => field.id === currentFieldName
      );
      const thisFieldIsNotAddValue = fieldEnding !== 'addValue';
      const startPositionField = `${currentFieldName}_startPosition`;
      const lengthField = `${currentFieldName}_length`;
      const positionFields = [startPositionField, lengthField];

      if (thisOptionalField && thisFieldIsNotAddValue) {
        if (!formState[startPositionField] && !formState[lengthField]) {
          asyncExecuteMultiplyGluFunctions(positionFields, unregisterValidators);
          return asyncExecuteMultiplyGluFunctions(positionFields, validate);
        }

        if (formState[startPositionField] || formState[lengthField]) {
          registerValidators(
            positionFields[0],
            startPositionFieldValidators(thisOptionalField, true)
          );
          return registerValidators(
            positionFields[1],
            lengthFieldValidators(thisOptionalField, true)
          );
        }
      }
      return null;
    },
    [
      registerValidators,
      unregisterValidators,
      validate,
      locale,
      formState,
      lengthFieldValidators,
      startPositionFieldValidators
    ]
  );

  const onPositionFieldChange = useCallback(
    (name) => {
      const fieldSplitted = name?.split('_');
      const currentFieldName = fieldSplitted[0];

      const tableFields =
        mapFormat === MAP_FORMAT.DELIMITED
          ? getDelimitedFormatTableFields(locale, true, true)
          : getFixedFormatTableFields(locale, true, true);

      const pickFieldsData = pick(
        formState,
        tableFields.filter((fixedFormatField) => fixedFormatField.name !== currentFieldName) // delete currentField because it always fails validation with itself,
      );

      const arrayOfFieldNames = Object.keys(pickFieldsData)
        .map((key) => (pickFieldsData[key] ? key : null))
        .filter(Boolean);

      return asyncExecuteMultiplyGluFunctions(arrayOfFieldNames, validate);
    },
    [formState, locale, validate, mapFormat]
  );

  const onFixedFieldFocus = useCallback(
    ({ target: { name } }) => {
      const fieldName = name?.split('_')?.[0];

      const tableFieldData = tableFields.find((field) => field.id === fieldName);

      registerFixedFieldValidators({
        tableFieldData,
        startPositionFieldValidators,
        lengthFieldValidators,
        registerValidators
      });

      unregisterValidators(`${tableFieldData.id}_addValue`);
      return validate(`${tableFieldData.id}_addValue`);
    },
    [
      validate,
      unregisterValidators,
      registerValidators,
      startPositionFieldValidators,
      lengthFieldValidators,
      tableFields
    ]
  );

  const handleFixedAddValueFieldFocus = useCallback(
    (fieldData) => {
      registerValidators(
        `${fieldData.id}_addValue`,
        fieldData.id === TABLE_FIELDS.TRAN_CODE && !isModeUpdateOnly
          ? requiredTransactionCodeValidatorFunc
          : addValueValidators(fieldData, fieldData?.maxLength)
      );

      const fixedFormatFieldsValidatorsData = [
        `${fieldData.id}_startPosition`,
        `${fieldData.id}_length`
      ];
      asyncExecuteMultiplyGluFunctions(fixedFormatFieldsValidatorsData, unregisterValidators);
      return asyncExecuteMultiplyGluFunctions(fixedFormatFieldsValidatorsData, validate);
    },
    [
      registerValidators,
      unregisterValidators,
      addValueValidators,
      validate,
      isModeUpdateOnly,
      requiredTransactionCodeValidatorFunc
    ]
  );

  const handleDelimitedAddValueFieldFocus = useCallback(
    (fieldData, validatorToRegister) => {
      registerValidators(`${fieldData.id}_addValue`, validatorToRegister);

      unregisterValidators(fieldData.id);
      return validate(fieldData.id);
    },
    [validate, unregisterValidators, registerValidators]
  );

  const onAddValueFieldFocus = useCallback(
    ({ target: { name } }) => {
      const fieldId = name?.split('_')?.[0];

      if (
        !formState[name] ||
        (fieldId === TABLE_FIELDS.AMOUNT && isAmountAddValueFieldEmptyMemoized)
      ) {
        const tableFieldData = tableFields.find((field) => field.id === fieldId);

        if (mapFormat === MAP_FORMAT.FIXED) {
          return handleFixedAddValueFieldFocus(tableFieldData);
        }

        return handleDelimitedAddValueFieldFocus(
          tableFieldData,
          addValueValidators(tableFieldData, tableFieldData?.maxLength)
        );
      }

      return null;
    },
    [
      isAmountAddValueFieldEmptyMemoized,
      addValueValidators,
      handleDelimitedAddValueFieldFocus,
      handleFixedAddValueFieldFocus,
      formState,
      tableFields,
      mapFormat
    ]
  );

  const fixedTableFields = useMemo(() => getFixedFormatTableFields(locale), [locale]);
  const delimitedTableFields = useMemo(() => getDelimitedFormatTableFields(locale), [locale]);

  // on first render we have to register all validators
  useEffect(() => {
    const tableFieldsToUnregister =
      mapFormat === MAP_FORMAT.DELIMITED ? fixedTableFields : delimitedTableFields;

    asyncExecuteMultiplyGluFunctions(tableFieldsToUnregister, unregisterValidators).then(() =>
      setInitialFieldsValidation({
        tableFields: tableFields.filter(
          (field) =>
            field.id !== TABLE_FIELDS.TRAN_CODE &&
            field.id !== TABLE_FIELDS.TRAN_TYPE &&
            field.id !== TABLE_FIELDS.ACCOUNT_TYPE
        ), // REMOVE TRAN_CODE, TRAN_TYPE, ACCOUNT_TYPE from INITIAL
        mapFormat,
        positionFieldValidators,
        startPositionFieldValidators,
        lengthFieldValidators,
        addValueValidators,
        requiredTransactionCodeValidatorFunc,
        registerValidators,
        formState: controlledFormData
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapFormat, controlledFormData]); // componentDidMount

  const turnOnCheckboxes = isModeAddAndUpdate || isModeUpdateOnly;
  const headerCellClass = turnOnCheckboxes ? classes.headerCellUpdate : classes.headerCell;

  const cellClass = turnOnCheckboxes
    ? classnames(classes.updateCell, { 'fixed-map': mapFormat === MAP_FORMAT.DELIMITED })
    : classes.cell;

  const memoizedTranCodeValidators = useMemo(
    () => ({
      startPosition: startPositionFieldValidators,
      lengthValidation: lengthFieldValidators,
      position: positionFieldValidators,
      addValue: requiredTransactionCodeValidatorFunc
    }),
    [
      startPositionFieldValidators,
      lengthFieldValidators,
      positionFieldValidators,
      requiredTransactionCodeValidatorFunc
    ]
  );

  const memoizedTranRowValidationHandlers = useMemo(
    () => ({
      delimited: {
        handleDelimitedAddValueFieldFocus
      },
      fixed: {
        handleFixedAddValueFieldFocus,
        onFixedFieldFocus
      }
    }),
    [handleDelimitedAddValueFieldFocus, handleFixedAddValueFieldFocus, onFixedFieldFocus]
  );

  const valueColLabel = turnOnCheckboxes
    ? locale['wireImports.replacementValue']
    : locale['wireImports.addValue'];

  return (
    <div
      className={classnames(
        classes.wrapper,
        mapFormat === MAP_FORMAT.FIXED ? 'fixed-map' : '',
        isModeUpdateOnly || isModeAddAndUpdate ? 'update-type' : ''
      )}
    >
      <div className={classes.header}>
        <div className={classnames(headerCellClass, cellClass)}>
          {locale['wireImports.fieldName']}
        </div>
        {turnOnCheckboxes && (
          <>
            <div className={classnames(headerCellClass, classes.updateCheckboxCell)}>
              {locale['wireImports.match']}
            </div>
            <div className={classnames(headerCellClass, classes.updateCheckboxCell)}>
              {locale['wireImports.update']}
            </div>
          </>
        )}
        {mapFormat === MAP_FORMAT.DELIMITED ? (
          <div className={classnames(headerCellClass, cellClass)}>
            {locale['wireImports.positionInImportFile']}
          </div>
        ) : (
          <>
            <div className={classnames(headerCellClass, cellClass)}>
              {locale['wireImports.startPosition']}
            </div>
            <div className={classnames(headerCellClass, cellClass)}>{locale['common.length']}</div>
          </>
        )}
        {transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRAN_TYPE && (
          <div className={classnames(headerCellClass, cellClass)}>
            {locale['wireImports.valueInImportFile']}
          </div>
        )}
        <div className={classnames(headerCellClass, cellClass)} data-qa="addValue-popover-block">
          {valueColLabel}
          <Tooltip
            className={classes.tooltipText}
            content={locale['wireImports.addValueExplanation']}
            placement="top"
          >
            <div className={classes.addValuePopover} data-qa="addValue-popover">
              <ExclamationCircleIcon className={classes.addValueIcon} />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className={classes.body}>
        {tableFields.map((field) => {
          const transactionRowProps = {
            key: field.id,
            field,
            cellClass,
            sending,
            formState,
            processMode,
            turnOnCheckboxes,
            onPositionFieldFocus,
            onPositionFieldChange,
            onChange,
            transactionFieldVariant,
            mapFormat,
            changeTransactionFieldVariant,
            registerValidators,
            unregisterValidators,
            validate,
            isModeUpdateOnly,
            handlers: memoizedTranRowValidationHandlers,
            valueColLabel
          };

          if (
            field.id === TABLE_FIELDS.TRAN_TYPE ||
            field.id === TABLE_FIELDS.ACCOUNT_TYPE ||
            field.id === TABLE_FIELDS.TRAN_CODE
          ) {
            if (
              transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRAN_TYPE &&
              (field.id === TABLE_FIELDS.TRAN_TYPE || field.id === TABLE_FIELDS.ACCOUNT_TYPE)
            ) {
              return <TransactionTypeRow {...transactionRowProps} />;
            }

            if (
              transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRANSACTION_CODE &&
              field.id === TABLE_FIELDS.TRAN_CODE
            ) {
              return (
                <TransactionCodeRow
                  {...transactionRowProps}
                  isModeUpdateOnly={isModeUpdateOnly}
                  validators={memoizedTranCodeValidators}
                />
              );
            }
          }
          if (
            field.id !== TABLE_FIELDS.TRAN_TYPE &&
            field.id !== TABLE_FIELDS.ACCOUNT_TYPE &&
            field.id !== TABLE_FIELDS.TRAN_CODE
          ) {
            return (
              <div key={field.id} className={classes.row}>
                <SharedRowPart
                  field={field}
                  fieldNameClassName={cellClass}
                  formState={formState}
                  onChange={onChange}
                  processMode={processMode}
                  sending={sending}
                  turnOnCheckboxes={turnOnCheckboxes}
                />

                {mapFormat === MAP_FORMAT.DELIMITED ? (
                  <div className={classnames(cellClass, classes.positionField)}>
                    <Input
                      data-qa={field.id}
                      disabled={
                        sending ||
                        (formState[`${field.id}_addValue`] &&
                          (field.id === TABLE_FIELDS.AMOUNT
                            ? !isAmountAddValueFieldEmptyMemoized
                            : true) && // for amount_addValue
                          get(formState[`${field.id}_addValue`], '[0].id') !== NONE)
                      }
                      htmlName={field.id}
                      maxLengthIndicatorLimit={POSITION_FIELD_MAX_LENGTH}
                      name={field.id}
                      onChange={onPositionFieldChange}
                      onFocus={onPositionFieldFocus}
                      suffixSymbol={null}
                    />
                  </div>
                ) : (
                  <>
                    <div className={classnames(cellClass, classes.positionField)}>
                      <Input
                        data-qa={`${field.id}_startPosition`}
                        disabled={
                          sending ||
                          (formState[`${field.id}_addValue`] &&
                            (field.id === TABLE_FIELDS.AMOUNT
                              ? !isAmountAddValueFieldEmptyMemoized
                              : true) && // for amount_addValue
                            get(formState[`${field.id}_addValue`], '[0].id') !== NONE)
                        }
                        htmlName={`${field.id}_startPosition`}
                        maxLengthIndicatorLimit={START_POSITION_MAX_LENGTH}
                        name={`${field.id}_startPosition`}
                        onBlur={onOptionalFixedPositionBlur}
                        onChange={onPositionFieldChange}
                        onFocus={onFixedFieldFocus}
                        suffixSymbol={null}
                      />
                    </div>
                    <div className={classnames(cellClass, classes.positionField)}>
                      <Input
                        data-qa={`${field.id}_length`}
                        disabled={
                          sending ||
                          (formState[`${field.id}_addValue`] &&
                            (field.id === TABLE_FIELDS.AMOUNT
                              ? !isAmountAddValueFieldEmptyMemoized
                              : true) && // for amount_addValue
                            get(formState[`${field.id}_addValue`], '[0].id') !== NONE)
                        }
                        htmlName={`${field.id}_length`}
                        maxLengthIndicatorLimit={
                          HARDCODED_LENGTH_FIELDS_LENGTH[field.id] || LENGTH_MAX_LENGTH
                        }
                        name={`${field.id}_length`}
                        onBlur={onOptionalFixedPositionBlur}
                        onChange={onPositionFieldChange}
                        onFocus={onFixedFieldFocus}
                        suffixSymbol={null}
                      />
                    </div>
                  </>
                )}

                {transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRAN_TYPE && (
                  <div className={classnames(cellClass)} />
                )}

                <div
                  className={classnames(
                    cellClass,
                    classes.addValueField,
                    field.id === TABLE_FIELDS.AMOUNT ? classes.amountAddValue : ''
                  )}
                >
                  {field.addValue && field.id === TABLE_FIELDS.AMOUNT ? (
                    <Currency
                      className={classes.currencyStyles}
                      data-qa={`${field.id}_addValue`}
                      disabled={
                        sending ||
                        (mapFormat === MAP_FORMAT.DELIMITED
                          ? formState[field.id]
                          : formState[`${field.id}_startPosition`] ||
                            formState[`${field.id}_length`])
                      }
                      htmlName={`${field.id}_addValue`}
                      labelText={field.name}
                      name={`${field.id}_addValue`}
                      onFocus={onAddValueFieldFocus}
                      screenReaderLabel
                    />
                  ) : null}

                  {field.addValue && field.id !== TABLE_FIELDS.AMOUNT ? (
                    field.type === TYPES.NUMBER ? (
                      <Input
                        className={classes.addValueField}
                        data-qa={`${field.id}_addValue`}
                        disabled={
                          sending ||
                          (mapFormat === MAP_FORMAT.DELIMITED
                            ? formState[field.id]
                            : formState[`${field.id}_startPosition`] ||
                              formState[`${field.id}_length`])
                        }
                        htmlName={`${field.id}_addValue`}
                        maxLengthIndicatorLimit={field?.maxLength}
                        name={`${field.id}_addValue`}
                        onFocus={onAddValueFieldFocus}
                        suffixSymbol={null}
                        type={field?.type}
                        validateBlurOnly
                      />
                    ) : (
                      <TextInput
                        className={classes.addValueField}
                        data-qa={`${field.id}_addValue`}
                        disabled={
                          sending ||
                          (mapFormat === MAP_FORMAT.DELIMITED
                            ? formState[field.id]
                            : formState[`${field.id}_startPosition`] ||
                              formState[`${field.id}_length`])
                        }
                        maxLengthIndicatorLimit={field?.maxLength}
                        name={`${field.id}_addValue`}
                        onFocus={onAddValueFieldFocus}
                        suffixSymbol={null}
                        type={field?.type}
                        validateBlurOnly
                      />
                    )
                  ) : null}
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};

TableComponent.propTypes = {
  transactionCodesData: PropTypes.shape({}),
  formState: PropTypes.shape({}),
  registerValidators: PropTypes.func,
  unregisterValidators: PropTypes.func,
  validate: PropTypes.func,
  sending: PropTypes.bool,
  modify: PropTypes.bool,
  processMode: PropTypes.string,
  onChange: PropTypes.func,
  mapFormat: PropTypes.string
};

export default TableComponent;
